import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Check, LogOut, Settings, User } from "lucide-react";
import { toast } from "sonner";
import { api, ApiError } from "@/api.js";
import {
  clearSession,
  getUser,
  setAccessToken,
  setActiveOrgSlug,
  setImpersonation,
  setUser,
} from "@/auth.js";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type Membership = {
  organisation_id: string;
  slug: string;
  name: string;
  role: string;
  is_examiner: boolean;
  status: string;
};

export function UserMenu() {
  const nav = useNavigate();
  const user = getUser();
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [switching, setSwitching] = useState<string | null>(null);
  const activeOrgId = user?.active_organisation_id ?? null;

  useEffect(() => {
    void api
      .me()
      .then((res) => {
        setMemberships(res.memberships);
        // Sync the cached role into localStorage so server-side role
        // changes (promotion to super_admin, demotion, org switch) take
        // effect on the next app boot without requiring a full sign-out
        // + sign-in. The RoleGated guards read from this cache, so
        // refresh-to-apply was the prior UX footgun.
        const current = getUser();
        if (current) {
          const active = res.active_organisation_id
            ? res.memberships.find(
                (m) => m.organisation_id === res.active_organisation_id,
              )
            : undefined;
          const roleChanged =
            !!active &&
            (active.role !== current.role ||
              active.is_examiner !== current.is_examiner);
          // Keep the verify-state cache authoritative too, so the banner / gate
          // reflect a verification (or grace lapse) that happened server-side.
          const verifyChanged =
            current.needs_verification !== res.user.needs_verification ||
            (current.verification_grace_until ?? null) !==
              res.user.verification_grace_until;
          if (roleChanged || verifyChanged) {
            setUser({
              ...current,
              ...(active
                ? {
                    role: active.role,
                    is_examiner: active.is_examiner,
                    active_organisation_id: res.active_organisation_id,
                  }
                : {}),
              needs_verification: res.user.needs_verification,
              verification_grace_until: res.user.verification_grace_until,
            });
          }
        }
        // Hydrate the impersonation flag from the server response. The
        // banner reads this on mount; without this sync, a page refresh
        // while shadowed would rely on stale localStorage (which is
        // fine) OR an expired server-side session would leave the
        // banner visible even after auto-close (not fine). Server is
        // authoritative.
        if (res.impersonation) {
          setImpersonation({
            target_organisation_id: res.impersonation.target_organisation_id,
            target_slug: res.impersonation.target_slug,
            started_at: res.impersonation.started_at,
            session_id: res.impersonation.session_id,
            reason: res.impersonation.reason,
            reason_category: res.impersonation.reason_category ?? null,
            view_as_role: res.impersonation.view_as_role ?? null,
            view_as_readonly: res.impersonation.view_as_readonly ?? true,
            expires_at: res.impersonation.expires_at ?? null,
          });
        } else {
          // Server says we're not impersonating — clear any local ghost.
          setImpersonation(null);
        }
      })
      .catch(() => {
        // Silently tolerate — menu still works without the list.
      });
  }, []);

  if (!user) return null;

  const initials = (user.full_name || user.email)
    .split(/[\s.@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("");

  async function signOut() {
    // Fire-and-forget server-side revoke — local clearSession is the source of
    // truth for the UI, but we call the API so the refresh token (in the
    // HttpOnly cookie) is invalidated in the master sessions table and can't
    // be used from a stolen client.
    try {
      await api.logout();
    } catch {
      // Ignore — sign-out UX shouldn't block on network.
    }
    clearSession();
    nav("/login", { replace: true });
  }

  async function switchOrg(slug: string, orgId: string, role: string, isExaminer: boolean) {
    if (switching) return;
    setSwitching(orgId);
    try {
      const res = await api.switchOrg(slug);
      setAccessToken(res.access_token);
      setActiveOrgSlug(slug);
      setUser({
        id: user!.id,
        email: user!.email,
        full_name: user!.full_name,
        role,
        is_examiner: isExaminer,
        active_organisation_id: res.active_organisation_id,
      });
      toast.success(`Switched to ${slug}`);
      // Reload so every page refetches with the new tenant context.
      window.location.assign("/dashboard");
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "switch failed");
      setSwitching(null);
    }
  }

  const activeMemberships = memberships.filter(
    (m) => m.status === "active",
  );
  const hasMultipleOrgs = activeMemberships.length > 1;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Account menu" className="rounded-full">
          <Avatar>
            <AvatarFallback>{initials || <User className="h-4 w-4" />}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span className="truncate text-sm font-medium">{user.full_name || "Signed in"}</span>
            <span className="truncate text-xs font-normal text-muted-foreground">{user.email}</span>
            {user.role && (
              <span className="mt-0.5 text-xs font-normal text-muted-foreground">
                Role: {user.role}
              </span>
            )}
          </div>
        </DropdownMenuLabel>
        {hasMultipleOrgs && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Switch organisation
            </DropdownMenuLabel>
            <DropdownMenuGroup>
              {activeMemberships.map((m) => {
                const active = m.organisation_id === activeOrgId;
                return (
                  <DropdownMenuItem
                    key={m.organisation_id}
                    disabled={active || switching !== null}
                    onClick={() => {
                      if (!active)
                        void switchOrg(m.slug, m.organisation_id, m.role, m.is_examiner);
                    }}
                  >
                    <Building2 className="mr-2 h-4 w-4" />
                    <div className="flex-1 truncate">
                      <div className="truncate">{m.name}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {m.slug} · {m.role.replace("_", " ")}
                      </div>
                    </div>
                    {active && <Check className="ml-2 h-4 w-4 text-primary" aria-hidden />}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuGroup>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => nav("/settings/profile")}>
          <Settings className="mr-2 h-4 w-4" />
          Profile &amp; settings
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => void signOut()}>
          <LogOut className="mr-2 h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
